import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useUpdateAtom } from 'jotai/utils';
import flatten from 'lodash/flatten';
import minBy from 'lodash/minBy';

import { Toast } from '@common/overlay-manager';

import { PdpBogoDiscountType } from '../graphql/select-option_types.2';
import {
  ActivateOptionAtom,
  ActivateRestockNotiDrawerAtom,
  AddItemAtom,
  ChangeItemCountsAtom,
  EnterOptionAtom,
  GoToOptionsAtom,
  RemoveAllItemsAtom,
  RemoveItemAtom,
  RequestRestockNotiAtom,
  resetAtoms,
  ResetOptionsAtom,
  resetSelectedOptionAtoms,
  SelectOptionAtom,
} from '../store/actionAtoms';
import {
  ActiveIndexAtom,
  ActiveParentIndexAtom,
  AdditionalOptionsAtom,
  BogoInfoAtom,
  BogoRequiredQuantityAtom,
  displayDeliveryTypeToggleButtonAtom,
  FirstSoldoutIndexAtom,
  IsItemsShownAtom,
  IsRestockNotiDrawerShownAtom,
  IsShowShippingDetailAtom,
  IsSingleSelectionAllowedAtom,
  MaximumCountAtom,
  OnlyZigzinOptionAtom,
  OptionsAtom,
  ProductItemListAtom,
  RestockNotiStatusAtom,
  TextOptionsAtom,
} from '../store/baseAtoms';
import {
  HasAdditionalFastShippingOptionAtom,
  hasAdditionalOptionsAtom,
  HasFastShippingOptionAtom,
  HasSelectedItemAtom,
  IsNoOptionAtom,
  ItemCardsAtom,
  OptionMatchedItemsAtom,
  OptionValuesAtom,
} from '../store/computedAtoms';
import { BogoInfo, CustomTextOption, ProductItem, SelectedItem, SelectedOption } from '../types/option-type';
import { ceilUnit } from '../utils/display';

/**
 * BOGO 정액/정률 할인 기준에 따른 금액 정보를 가져오는 함수
 * BOGO 기본 할인가는 50%, 그 외는 정액/정률 할인 기준에 따라 기본 금액에서 할인시킨다.
 */
function getBogoDealPrice(price: number, bogoInfo: BogoInfo | null) {
  let refinedPrice = price / ((bogoInfo?.required_quantity ?? 0) + 1); // default: BOGO 50% 할인

  if (!bogoInfo?.discount_type) {
    return refinedPrice;
  }

  // 정액할인
  if (bogoInfo.discount_type === PdpBogoDiscountType.AMOUNT) {
    const discountAmount = bogoInfo.discount_amount ?? 0;

    refinedPrice = price - discountAmount;
  }

  // 정률할인
  if (bogoInfo.discount_type === PdpBogoDiscountType.RATE) {
    const BASIS_POINT = 10000;
    const rate = (BASIS_POINT - (bogoInfo.discount_rate_bp ?? 0)) / BASIS_POINT;

    refinedPrice = price * rate;
  }

  return ceilUnit(refinedPrice);
}

function getItemPrice({
  isBogo,
  bogoInfo,
  minPrice,
  items,
  defaultPrice,
}: {
  isBogo: boolean;
  bogoInfo: BogoInfo | null;
  minPrice: number;
  items: ProductItem[];
  defaultPrice: number;
}) {
  if (!isBogo) return defaultPrice;

  return getBogoDealPrice(minPrice, bogoInfo) * items.length;
}

/**
 * BOGO 가장 늦게 배송되는 배송 타입/문구를 반환하는 함수
 */
function findLatestShippingItem(item: ProductItem[]) {
  const [firstItem, secondItem] = item;

  if (item.length === 1) {
    return firstItem;
  }
  if ((firstItem?.expected_delivery_time ?? 0) > (secondItem?.expected_delivery_time ?? 0)) {
    return firstItem;
  } else {
    return secondItem;
  }
}

export function useOptionsControl() {
  const [restockNotiStatus, setRestockNotiStatus] = useAtom(RestockNotiStatusAtom);
  const isRestockNotiDrawerShown = useAtomValue(IsRestockNotiDrawerShownAtom);
  const [currentOptionMatchedItem, setCurrentOptionMatchedItem] = useState<SelectedItem | null>(null);
  const [isShowShippingDetail, setIsShowShippingDetail] = useAtom(IsShowShippingDetailAtom);
  const requestRestockNoti = useUpdateAtom(RequestRestockNotiAtom);
  const [isItemsShown, setIsItemsShown] = useAtom(IsItemsShownAtom);
  const [, setMaximumCount] = useAtom(MaximumCountAtom);
  const [bogoInfo, setBogoInfo] = useAtom(BogoInfoAtom);
  const firstSoldOutIndex = useAtomValue(FirstSoldoutIndexAtom);
  const textOptions = useAtomValue(TextOptionsAtom);
  const options = useAtomValue(OptionsAtom);
  const additional_options = useAtomValue(AdditionalOptionsAtom);
  const itemCards = useAtomValue(ItemCardsAtom);
  const optionValues = useAtomValue(OptionValuesAtom);

  const activateOption = useUpdateAtom(ActivateOptionAtom);
  const [activeIndex, setActiveIndex] = useAtom(ActiveIndexAtom);
  const [activeParentIndex, setActiveParentIndex] = useAtom(ActiveParentIndexAtom);
  const [checkedOnlyZigzinOption, setCheckedOnlyZigzinOption] = useAtom(OnlyZigzinOptionAtom);
  const itemList = useAtomValue(ProductItemListAtom);

  const has_additional_options = useAtomValue(hasAdditionalOptionsAtom);
  const has_selected_item = useAtomValue(HasSelectedItemAtom);
  const [displayDeliveryTypeToggleButton, setDisplayDeliveryTypeToggleButton] = useAtom(
    displayDeliveryTypeToggleButtonAtom,
  );
  const initialize = useUpdateAtom(resetAtoms);
  const [bogo_required_quantity, setBogoRequiredQuantity] = useAtom(BogoRequiredQuantityAtom);
  const is_bogo = typeof bogo_required_quantity === 'number';
  const isNoOption = useAtomValue(IsNoOptionAtom);
  const optionMatchedItem = useAtomValue(OptionMatchedItemsAtom);

  const hasFastShippingOption = useAtomValue(HasFastShippingOptionAtom);
  const hasAdditionalFastShippingOption = useAtomValue(HasAdditionalFastShippingOptionAtom);

  const removeItem = useUpdateAtom(RemoveItemAtom);
  const removeAllItems = useUpdateAtom(RemoveAllItemsAtom);
  const addItem = useUpdateAtom(AddItemAtom);
  const selectOption = useUpdateAtom(SelectOptionAtom);
  const goToOptionByIndex = useUpdateAtom(GoToOptionsAtom);
  const activateRestockNotiDrawer = useUpdateAtom(ActivateRestockNotiDrawerAtom);
  const resetOptions = useUpdateAtom(ResetOptionsAtom);
  const enterOption = useUpdateAtom(EnterOptionAtom);
  const changeItemCounts = useUpdateAtom(ChangeItemCountsAtom);
  const resetSelectedOption = useUpdateAtom(resetSelectedOptionAtoms);
  const setIsSingleSelectionAllowed = useUpdateAtom(IsSingleSelectionAllowedAtom);
  const itemCardTotalCount = itemCards.reduce((cur, a) => cur + a.count, 0);

  const bogoMinPrice = minBy(itemList, 'price')?.price ?? 0; // BOGO는 상품 가격 중 최저가를 기준으로 금액 설정
  const refined_options = isRestockNotiDrawerShown ? options.slice(0, 1) : options;

  const isAccessibleToOptions = useMemo(() => {
    return textOptions.map((o) => !o.find(({ is_required, selected_value }) => is_required && !selected_value));
  }, [textOptions]);

  useEffect(() => {
    if (checkedOnlyZigzinOption && optionValues.length === 0) {
      resetSelectedOption();
      setActiveIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedOnlyZigzinOption, optionValues]);

  const makeSelectedItem = useCallback(
    (
      items: ProductItem[],
      count: number,
      options: {
        options: SelectedOption[][];
        text_options: CustomTextOption[][];
        is_bogo: boolean;
      },
    ) => {
      const [default_item] = items;
      const name = items.map((item) => item.name).join(', ');
      const latestShippingItem = findLatestShippingItem(items);
      const price = getItemPrice({
        isBogo: options.is_bogo,
        bogoInfo,
        items: items,
        minPrice: bogoMinPrice,
        defaultPrice: default_item.price,
      });

      const originItems = items.map((o) => ({
        ...o,
        price: options.is_bogo ? price : o.price,
      }));

      return {
        ...default_item,
        ...options,
        price,
        name,
        count,
        origin_items: originItems,
        is_bogo: options.is_bogo,
        delivery_type: latestShippingItem.delivery_type,
        expected_delivery_date: latestShippingItem.expected_delivery_date,
      };
    },
    [bogoInfo, bogoMinPrice],
  );

  const isNecessaryOptionSelected = (parent_key: number, child_key: number) => {
    const length = options[0].length;
    const refinedOptions = flatten(options);
    const currentOptions = refinedOptions.slice(0, parent_key * length + child_key);
    const hasNoRequiredOptions = currentOptions.every(({ required }) => !required);
    const allOptionsSelected = currentOptions.every(({ selected_value }) => !!selected_value);

    // 비필수옵션 선택 처리를 위한 조건처리
    if (hasNoRequiredOptions) return true;

    return allOptionsSelected;
  };

  const checkSelectBoxActive = (parent_key: number, child_key: number) => {
    return activeParentIndex === parent_key && child_key === activeIndex;
  };

  const validateRequiredTextOptions = (parent_key: number) => {
    return textOptions[parent_key]?.find(({ is_required, selected_value }) => is_required && !selected_value);
  };

  const checkAccessible = (parent_key: number, child_key: number) => {
    const hasRequiredTextOptions = textOptions[parent_key].some(({ is_required }) => is_required);

    if (hasRequiredTextOptions) {
      return isAccessibleToOptions[parent_key] && isNecessaryOptionSelected(parent_key, child_key);
    }

    return isNecessaryOptionSelected(parent_key, child_key);
  };

  const handleOptionClick = (parent_key: number, child_key: number) => () => {
    if (!isAccessibleToOptions[parent_key]) {
      return Toast.show('필수 옵션 정보를 입력해 주세요.');
    }

    if (!isNecessaryOptionSelected(parent_key, child_key)) {
      Toast.show('상위 옵션을 선택해 주세요.');
      return;
    }

    goToOptionByIndex({
      parent_key,
      child_key,
    });
  };

  const resetAll = () => {
    resetOptions();
    setActiveParentIndex(0);
    setActiveIndex(0);
  };

  return {
    is_bogo,
    options,
    refined_options,
    additional_options,
    bogoMinPrice,
    textOptions,
    optionValues,
    bogo_required_quantity,
    optionMatchedItem,
    currentOptionMatchedItem,
    itemCards,
    itemList,
    bogoInfo,

    isNoOption,
    isRestockNotiDrawerShown,
    isShowShippingDetail,
    isItemsShown,
    restockNotiStatus,
    firstSoldOutIndex,
    activateOption,
    has_additional_options,
    has_selected_item,
    checkedOnlyZigzinOption,
    displayDeliveryTypeToggleButton,
    setDisplayDeliveryTypeToggleButton,
    requestRestockNoti,
    setIsShowShippingDetail,
    setBogoInfo,
    setBogoRequiredQuantity,
    setIsItemsShown,
    setMaximumCount,
    setRestockNotiStatus,
    setCheckedOnlyZigzinOption,
    setActiveIndex,
    setActiveParentIndex,
    setCurrentOptionMatchedItem,
    addItem,
    removeItem,
    removeAllItems,
    changeItemCounts,
    goToOptionByIndex,
    activateRestockNotiDrawer,
    resetOptions,
    selectOption,
    resetAll,
    enterOption,
    checkSelectBoxActive,
    checkAccessible,
    initialize,
    handleOptionClick,
    makeSelectedItem,
    validateRequiredTextOptions,
    itemCardTotalCount,
    hasFastShippingOption,
    hasAdditionalFastShippingOption,
    setIsSingleSelectionAllowed,
  };
}
